import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  CONDITIONS, PART_TIERS, ROAD_LEGALITY, SOLD_AS, COUNTRIES, regionsForCountry,
  BOLT_PATTERNS, WHEELS_DEPARTMENT_SLUG,
} from '../constants/marketplace';
import {
  createListing, updateListing, getListing, uploadListingImages,
  deleteListingImage, getGarageVehicles,
} from '../services/marketplace';
import MarketplaceCategoryPicker from '../components/marketplace/MarketplaceCategoryPicker';

// ─── Icons (inline SVG, Lucide/Feather style — never emojis) ────────────────────
const CloseIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const ChevronRight = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const CameraIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);
const PlusIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const CarIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 17H3v-5l2-5h14l2 5v5h-2" />
    <circle cx="7.5" cy="17.5" r="1.5" /><circle cx="16.5" cy="17.5" r="1.5" />
    <path d="M5 12h14" />
  </svg>
);

// ─── Small building blocks ──────────────────────────────────────────────────────
function Chip({ label, active, onClick }) {
  return (
    <button type="button" className={`mc-chip${active ? ' is-active' : ''}`} onClick={onClick}>
      {label}
    </button>
  );
}
function Field({ label, children, className = '' }) {
  return (
    <div className={`mc-field ${className}`}>
      <label className="mc-label">{label}</label>
      {children}
    </div>
  );
}
function Toggle({ label, value, onChange }) {
  return (
    <button type="button" className="mc-toggle" onClick={() => onChange(!value)}>
      <span className="mc-toggle-label">{label}</span>
      <span className={`mc-switch${value ? ' is-on' : ''}`}><span className="mc-knob" /></span>
    </button>
  );
}

export default function MarketplaceCreate() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = !!id;
  const user = useSelector((s) => s.auth.user);

  // Images: newly picked File objects (with preview URLs), plus server images in edit mode.
  const [files, setFiles] = useState([]); // [{ file, preview }]
  const [existingImages, setExistingImages] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);

  const [loadingListing, setLoadingListing] = useState(editing);
  const [showCatPicker, setShowCatPicker] = useState(false);
  const [category, setCategory] = useState(null); // { id, slug, name, departmentSlug }

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [brand, setBrand] = useState('');
  const [partNumber, setPartNumber] = useState('');
  const [condition, setCondition] = useState('used');
  const [partTier, setPartTier] = useState('');
  const [roadLegality, setRoadLegality] = useState('street');

  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('AUD');
  const [isNegotiable, setIsNegotiable] = useState(false);
  const [quantity, setQuantity] = useState('1');
  const [soldAs, setSoldAs] = useState('');

  // Core charge
  const [coreRequired, setCoreRequired] = useState(false);
  const [coreDeposit, setCoreDeposit] = useState('');

  // Fitment
  const [isUniversal, setIsUniversal] = useState(false);
  const [fitments, setFitments] = useState([]); // { make, model, yearStart, yearEnd, badge, engineCode, position, note }
  const [showGarage, setShowGarage] = useState(false);
  const [garage, setGarage] = useState(null);

  // Wheels
  const [boltPattern, setBoltPattern] = useState('');
  const [wheelDiameter, setWheelDiameter] = useState('');
  const [wheelWidth, setWheelWidth] = useState('');
  const [wheelOffset, setWheelOffset] = useState('');
  const [centerBore, setCenterBore] = useState('');
  const [isStaggered, setIsStaggered] = useState(false);
  const [rearDiameter, setRearDiameter] = useState('');
  const [rearWidth, setRearWidth] = useState('');
  const [rearOffset, setRearOffset] = useState('');

  // Location & delivery
  const [country, setCountry] = useState('AU');
  const [region, setRegion] = useState('');
  const [suburb, setSuburb] = useState('');
  const [postcode, setPostcode] = useState('');
  const [deliveryPickup, setDeliveryPickup] = useState(true);
  const [deliveryShipping, setDeliveryShipping] = useState(false);
  const [shippingCost, setShippingCost] = useState('');

  const [submitting, setSubmitting] = useState(false);

  const isWheel = category?.departmentSlug === WHEELS_DEPARTMENT_SLUG;

  // Revoke preview object URLs on unmount to avoid leaks.
  useEffect(() => () => { files.forEach((f) => URL.revokeObjectURL(f.preview)); }, [files]);

  // Edit mode — load the existing listing and prefill every field.
  useEffect(() => {
    if (!editing) return undefined;
    let active = true;
    getListing(id).then(({ listing: l }) => {
      if (!active || !l) return;
      const slug = l.categorySlug || l.category?.slug || null;
      if (slug) {
        setCategory({
          id: l.categoryId || l.category?.id || null,
          slug,
          name: l.category?.name || slug.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          departmentSlug: l.departmentSlug || l.category?.departmentSlug || '',
        });
      }
      setTitle(l.title || '');
      setDescription(l.description || '');
      setBrand(l.brand || '');
      setPartNumber(l.partNumber || '');
      setCondition(l.condition || 'used');
      setPartTier(l.partTier || '');
      setRoadLegality(l.roadLegality || 'street');
      setPrice(l.price != null ? String(Number(l.price)) : '');
      setCurrency(l.currency || 'AUD');
      setIsNegotiable(!!l.isNegotiable);
      setQuantity(l.quantity != null ? String(l.quantity) : '1');
      setSoldAs(l.soldAs || '');
      setCoreRequired(!!l.coreRequired);
      setCoreDeposit(l.coreDeposit != null ? String(Number(l.coreDeposit)) : '');
      setIsUniversal(!!l.isUniversal);
      setFitments((l.fitments || []).map((f) => ({
        make: f.make || '', model: f.model || '',
        yearStart: f.yearStart != null ? String(f.yearStart) : '',
        yearEnd: f.yearEnd != null ? String(f.yearEnd) : '',
        badge: f.badge || '', engineCode: f.engineCode || '',
        position: f.position || '', note: f.note || '',
      })));
      setBoltPattern(l.wheelBoltPattern || '');
      setWheelDiameter(l.wheelDiameterIn != null ? String(Number(l.wheelDiameterIn)) : '');
      setWheelWidth(l.wheelWidthIn != null ? String(Number(l.wheelWidthIn)) : '');
      setWheelOffset(l.wheelOffsetMm != null ? String(Number(l.wheelOffsetMm)) : '');
      setCenterBore(l.wheelCenterBoreMm != null ? String(Number(l.wheelCenterBoreMm)) : '');
      setIsStaggered(!!l.isStaggered);
      setRearDiameter(l.wheelRearDiameterIn != null ? String(Number(l.wheelRearDiameterIn)) : '');
      setRearWidth(l.wheelRearWidthIn != null ? String(Number(l.wheelRearWidthIn)) : '');
      setRearOffset(l.wheelRearOffsetMm != null ? String(Number(l.wheelRearOffsetMm)) : '');
      setCountry(l.country || 'AU');
      setRegion(l.region || '');
      setSuburb(l.suburb || '');
      setPostcode(l.postcode || '');
      setDeliveryPickup(l.deliveryPickup != null ? !!l.deliveryPickup : true);
      setDeliveryShipping(!!l.deliveryShipping);
      setShippingCost(l.shippingCost != null ? String(Number(l.shippingCost)) : '');
      setExistingImages(l.images || []);
    }).catch(() => {
      toast.error('Could not load the listing to edit.');
    }).finally(() => { if (active) setLoadingListing(false); });
    return () => { active = false; };
  }, [editing, id]);

  const pickImages = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setFiles((prev) => {
      const room = Math.max(0, 10 - prev.length - existingImages.length);
      const next = picked.slice(0, room).map((file) => ({ file, preview: URL.createObjectURL(file) }));
      return [...prev, ...next];
    });
    e.target.value = ''; // allow re-picking the same file
  };
  const removeFile = (i) => setFiles((prev) => {
    const f = prev[i];
    if (f) URL.revokeObjectURL(f.preview);
    return prev.filter((_, idx) => idx !== i);
  });
  const removeExisting = (imgId) => {
    setRemovedImageIds((r) => [...r, imgId]);
    setExistingImages((p) => p.filter((x) => x.id !== imgId));
  };

  const addFitment = () => setFitments((f) => [...f, { make: '', model: '', yearStart: '', yearEnd: '', badge: '', engineCode: '', position: '', note: '' }]);
  const updateFitment = (i, k, v) => setFitments((f) => f.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const removeFitment = (i) => setFitments((f) => f.filter((_, idx) => idx !== i));

  const openGarage = useCallback(async () => {
    setShowGarage(true);
    if (garage === null && user?.id) {
      try {
        const data = await getGarageVehicles(user.id);
        setGarage(data.vehicles || (Array.isArray(data) ? data : []));
      } catch { setGarage([]); }
    }
  }, [garage, user]);

  const addFromGarage = (uv) => {
    const v = uv.vehicle || uv;
    setFitments((f) => [...f, {
      make: v.make || '', model: v.model || '',
      yearStart: v.year ? String(v.year) : '', yearEnd: v.year ? String(v.year) : '',
      badge: '', engineCode: '', position: '', note: '',
    }]);
    setIsUniversal(false);
    setShowGarage(false);
  };

  const submit = async () => {
    if (!category) { toast.error('Choose what kind of part this is.'); return; }
    if (!title.trim()) { toast.error('Give your listing a title.'); return; }
    if (!price || isNaN(parseFloat(price))) { toast.error('Enter a valid price.'); return; }

    setSubmitting(true);
    try {
      const body = {
        categorySlug: category?.slug || null,
        categoryId: category?.id || null,
        title: title.trim(),
        description: description.trim() || null,
        brand: brand.trim() || null,
        partNumber: partNumber.trim() || null,
        condition,
        partTier: partTier || null,
        roadLegality,
        price: parseFloat(price),
        currency,
        isNegotiable,
        quantity: parseInt(quantity, 10) || 1,
        soldAs: soldAs || null,
        coreRequired,
        coreDeposit: coreRequired && coreDeposit ? parseFloat(coreDeposit) : null,
        isUniversal,
        fitments: isUniversal ? [] : fitments.filter((f) => f.make && f.make.trim()).map((f) => ({
          make: f.make.trim(),
          model: f.model.trim() || null,
          yearStart: f.yearStart ? parseInt(f.yearStart, 10) : null,
          yearEnd: f.yearEnd ? parseInt(f.yearEnd, 10) : null,
          badge: f.badge.trim() || null,
          engineCode: f.engineCode.trim() || null,
          position: f.position.trim() || null,
          note: f.note.trim() || null,
        })),
        country,
        region: region || null,
        suburb: suburb.trim() || null,
        postcode: postcode.trim() || null,
        deliveryPickup,
        deliveryShipping,
        shippingCost: deliveryShipping && shippingCost ? parseFloat(shippingCost) : null,
      };
      if (isWheel) {
        const lug = boltPattern ? parseInt(boltPattern.split('x')[0], 10) : null;
        const pcd = boltPattern && boltPattern.includes('x') ? parseFloat(boltPattern.split('x')[1]) : null;
        Object.assign(body, {
          wheelBoltPattern: boltPattern || null,
          wheelLugCount: lug || null,
          wheelPcdMm: pcd || null,
          wheelDiameterIn: wheelDiameter ? parseFloat(wheelDiameter) : null,
          wheelWidthIn: wheelWidth ? parseFloat(wheelWidth) : null,
          wheelOffsetMm: wheelOffset !== '' ? parseFloat(wheelOffset) : null,
          wheelCenterBoreMm: centerBore ? parseFloat(centerBore) : null,
          isStaggered,
          wheelRearDiameterIn: isStaggered && rearDiameter ? parseFloat(rearDiameter) : null,
          wheelRearWidthIn: isStaggered && rearWidth ? parseFloat(rearWidth) : null,
          wheelRearOffsetMm: isStaggered && rearOffset !== '' ? parseFloat(rearOffset) : null,
        });
      }

      const uploadNewImages = async (listingId) => {
        if (!files.length) return;
        const fd = new FormData();
        files.forEach((f) => fd.append('images', f.file));
        await uploadListingImages(listingId, fd);
      };

      if (editing) {
        await updateListing(id, body);
        for (const imgId of removedImageIds) {
          try { await deleteListingImage(id, imgId); } catch { /* best-effort */ }
        }
        await uploadNewImages(id);
        navigate(`/marketplace/${id}`);
      } else {
        const { listing } = await createListing(body);
        await uploadNewImages(listing.id);
        navigate(`/marketplace/${listing.id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || (editing ? 'Could not save changes.' : 'Could not create listing.'));
    } finally {
      setSubmitting(false);
    }
  };

  const totalImages = existingImages.length + files.length;

  return (
    <div className="mc-page">
      <div className="mc-shell">
        <div className="mc-topbar">
          <button type="button" className="mc-iconbtn" onClick={() => navigate(-1)} aria-label="Close">
            <CloseIcon />
          </button>
          <h1 className="mc-title">{editing ? 'Edit listing' : 'New listing'}</h1>
          <span className="mc-topbar-spacer" />
        </div>

        {editing && loadingListing ? (
          <div className="mc-loading"><div className="loading-spinner" /></div>
        ) : (
          <div className="mc-form">
            {/* Photos */}
            <div className="mc-photo-row">
              <label className="mc-add-photo">
                <input type="file" multiple accept="image/*" onChange={pickImages} disabled={totalImages >= 10} hidden />
                <CameraIcon />
                <span>Add photos</span>
              </label>
              {existingImages.map((im) => (
                <div key={`ex-${im.id}`} className="mc-thumb">
                  <img src={im.thumbnailUrl || im.imageUrl} alt="" />
                  <button type="button" className="mc-thumb-remove" onClick={() => removeExisting(im.id)} aria-label="Remove photo">
                    <CloseIcon size={14} />
                  </button>
                </div>
              ))}
              {files.map((f, i) => (
                <div key={`new-${i}`} className="mc-thumb">
                  <img src={f.preview} alt="" />
                  <button type="button" className="mc-thumb-remove" onClick={() => removeFile(i)} aria-label="Remove photo">
                    <CloseIcon size={14} />
                  </button>
                </div>
              ))}
            </div>

            {/* Category */}
            <button type="button" className="mc-cat-btn" onClick={() => setShowCatPicker(true)}>
              <div>
                <span className="mc-cat-label">Category</span>
                <span className={`mc-cat-value${category ? '' : ' is-placeholder'}`}>
                  {category ? category.name : 'Select a category'}
                </span>
              </div>
              <ChevronRight />
            </button>

            <Field label="Title">
              <input className="mc-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Volk TE37 18x9.5 +22" />
            </Field>
            <Field label="Description">
              <textarea className="mc-input mc-textarea" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Condition notes, history, reason for selling…" rows={4} />
            </Field>

            <div className="mc-row2">
              <Field label="Brand"><input className="mc-input" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Brand" /></Field>
              <Field label="Part number"><input className="mc-input" value={partNumber} onChange={(e) => setPartNumber(e.target.value)} placeholder="OEM / part #" /></Field>
            </div>

            <Field label="Condition">
              <div className="mc-chip-wrap">
                {CONDITIONS.map((o) => <Chip key={o.value} label={o.label} active={condition === o.value} onClick={() => setCondition(o.value)} />)}
              </div>
            </Field>
            <Field label="Type">
              <div className="mc-chip-wrap">
                {PART_TIERS.map((o) => <Chip key={o.value} label={o.label} active={partTier === o.value} onClick={() => setPartTier(partTier === o.value ? '' : o.value)} />)}
              </div>
            </Field>
            <Field label="Road legality">
              <div className="mc-chip-wrap">
                {ROAD_LEGALITY.map((o) => <Chip key={o.value} label={o.label} active={roadLegality === o.value} onClick={() => setRoadLegality(o.value)} />)}
              </div>
            </Field>

            {/* Wheel specs (only when the category sits under the wheels department) */}
            {isWheel && (
              <div className="mc-wheelbox">
                <div className="mc-section-title">Wheel fitment</div>
                <Field label="Bolt pattern (PCD)">
                  <div className="mc-chip-wrap">
                    {BOLT_PATTERNS.map((b) => <Chip key={b} label={b} active={boltPattern === b} onClick={() => setBoltPattern(boltPattern === b ? '' : b)} />)}
                  </div>
                </Field>
                <div className="mc-row3">
                  <Field label={'Diameter "'}><input className="mc-input" value={wheelDiameter} onChange={(e) => setWheelDiameter(e.target.value)} inputMode="decimal" placeholder="18" /></Field>
                  <Field label={'Width "'}><input className="mc-input" value={wheelWidth} onChange={(e) => setWheelWidth(e.target.value)} inputMode="decimal" placeholder="9.5" /></Field>
                  <Field label="Offset mm"><input className="mc-input" value={wheelOffset} onChange={(e) => setWheelOffset(e.target.value)} inputMode="text" placeholder="22" /></Field>
                </div>
                <Field label="Centre bore mm">
                  <input className="mc-input" value={centerBore} onChange={(e) => setCenterBore(e.target.value)} inputMode="decimal" placeholder="73.1" />
                </Field>
                <Field label="Sold as">
                  <div className="mc-chip-wrap">
                    {SOLD_AS.map((o) => <Chip key={o.value} label={o.label} active={soldAs === o.value} onClick={() => setSoldAs(soldAs === o.value ? '' : o.value)} />)}
                  </div>
                </Field>
                <Toggle label="Staggered (different front / rear)" value={isStaggered} onChange={setIsStaggered} />
                {isStaggered && (
                  <div className="mc-row3">
                    <Field label={'Rear diameter "'}><input className="mc-input" value={rearDiameter} onChange={(e) => setRearDiameter(e.target.value)} inputMode="decimal" placeholder="18" /></Field>
                    <Field label={'Rear width "'}><input className="mc-input" value={rearWidth} onChange={(e) => setRearWidth(e.target.value)} inputMode="decimal" placeholder="10.5" /></Field>
                    <Field label="Rear offset mm"><input className="mc-input" value={rearOffset} onChange={(e) => setRearOffset(e.target.value)} inputMode="text" placeholder="15" /></Field>
                  </div>
                )}
              </div>
            )}

            {/* Price */}
            <div className="mc-row2">
              <Field label="Price"><input className="mc-input" value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal" placeholder="0" /></Field>
              <Field label="Currency">
                <div className="mc-chip-wrap">
                  {COUNTRIES.map((c) => {
                    const cur = c.value === 'AU' ? 'AUD' : 'NZD';
                    return <Chip key={c.value} label={cur} active={currency === cur} onClick={() => setCurrency(cur)} />;
                  })}
                </div>
              </Field>
            </div>
            <div className="mc-row2">
              <Field label="Quantity"><input className="mc-input" value={quantity} onChange={(e) => setQuantity(e.target.value)} inputMode="numeric" placeholder="1" /></Field>
              {!isWheel && (
                <Field label="Sold as">
                  <div className="mc-chip-wrap">
                    {SOLD_AS.map((o) => <Chip key={o.value} label={o.label} active={soldAs === o.value} onClick={() => setSoldAs(soldAs === o.value ? '' : o.value)} />)}
                  </div>
                </Field>
              )}
            </div>
            <Toggle label="Price negotiable" value={isNegotiable} onChange={setIsNegotiable} />

            {/* Core charge */}
            <Toggle label="Core charge / exchange required" value={coreRequired} onChange={setCoreRequired} />
            {coreRequired && (
              <Field label="Core deposit"><input className="mc-input" value={coreDeposit} onChange={(e) => setCoreDeposit(e.target.value)} inputMode="decimal" placeholder="0" /></Field>
            )}

            {/* Fitment */}
            <div className="mc-field">
              <div className="mc-fit-header">
                <label className="mc-label">Fitment — what it fits</label>
                <Toggle label="Universal (fits all)" value={isUniversal} onChange={setIsUniversal} />
              </div>
              {!isUniversal ? (
                <>
                  {fitments.map((f, i) => (
                    <div key={i} className="mc-fit-card">
                      <div className="mc-row2">
                        <input className="mc-input" value={f.make} onChange={(e) => updateFitment(i, 'make', e.target.value)} placeholder="Make" />
                        <input className="mc-input" value={f.model} onChange={(e) => updateFitment(i, 'model', e.target.value)} placeholder="Model (optional)" />
                      </div>
                      <div className="mc-row2">
                        <input className="mc-input" value={f.yearStart} onChange={(e) => updateFitment(i, 'yearStart', e.target.value)} inputMode="numeric" placeholder="Year from" />
                        <input className="mc-input" value={f.yearEnd} onChange={(e) => updateFitment(i, 'yearEnd', e.target.value)} inputMode="numeric" placeholder="Year to" />
                      </div>
                      <div className="mc-row2">
                        <input className="mc-input" value={f.badge} onChange={(e) => updateFitment(i, 'badge', e.target.value)} placeholder="Badge / variant (optional)" />
                        <input className="mc-input" value={f.engineCode} onChange={(e) => updateFitment(i, 'engineCode', e.target.value)} placeholder="Engine code (optional)" />
                      </div>
                      <div className="mc-row2">
                        <input className="mc-input" value={f.position} onChange={(e) => updateFitment(i, 'position', e.target.value)} placeholder="Position (optional)" />
                        <input className="mc-input" value={f.note} onChange={(e) => updateFitment(i, 'note', e.target.value)} placeholder="Note (optional)" />
                      </div>
                      <button type="button" className="mc-fit-remove" onClick={() => removeFitment(i)}>Remove</button>
                    </div>
                  ))}
                  <div className="mc-fit-actions">
                    <button type="button" className="mc-small-btn" onClick={addFitment}><PlusIcon /> Add vehicle</button>
                    <button type="button" className="mc-small-btn" onClick={openGarage}><CarIcon /> From my garage</button>
                  </div>
                </>
              ) : (
                <p className="mc-muted">This part is listed as universal — it fits most vehicles.</p>
              )}
            </div>

            {/* Location */}
            <Field label="Country">
              <div className="mc-chip-wrap">
                {COUNTRIES.map((c) => (
                  <Chip key={c.value} label={c.label} active={country === c.value}
                    onClick={() => { setCountry(c.value); setRegion(''); setCurrency(c.value === 'AU' ? 'AUD' : 'NZD'); }} />
                ))}
              </div>
            </Field>
            <Field label="Region">
              <div className="mc-chip-wrap">
                {regionsForCountry(country).map((r) => <Chip key={r} label={r} active={region === r} onClick={() => setRegion(region === r ? '' : r)} />)}
              </div>
            </Field>
            <div className="mc-row2">
              <Field label="Suburb"><input className="mc-input" value={suburb} onChange={(e) => setSuburb(e.target.value)} placeholder="Suburb" /></Field>
              <Field label="Postcode"><input className="mc-input" value={postcode} onChange={(e) => setPostcode(e.target.value)} inputMode="numeric" placeholder="Postcode" /></Field>
            </div>

            {/* Delivery */}
            <Toggle label="Available for pickup" value={deliveryPickup} onChange={setDeliveryPickup} />
            <Toggle label="Can post / ship" value={deliveryShipping} onChange={setDeliveryShipping} />
            {deliveryShipping && (
              <Field label="Shipping cost"><input className="mc-input" value={shippingCost} onChange={(e) => setShippingCost(e.target.value)} inputMode="decimal" placeholder="0" /></Field>
            )}

            <button type="button" className="mc-submit" onClick={submit} disabled={submitting}>
              {submitting ? 'Saving…' : (editing ? 'Save changes' : 'Post listing')}
            </button>
          </div>
        )}
      </div>

      {/* Mount only while open — the picker fetches the whole category tree on mount,
          and it closes itself once onChange fires. */}
      {showCatPicker && (
        <MarketplaceCategoryPicker
          value={category}
          onChange={(node) => setCategory({
            id: node.id,
            slug: node.slug,
            name: node.name,
            departmentSlug: node.departmentSlug,
          })}
          onClose={() => setShowCatPicker(false)}
        />
      )}

      {showGarage && (
        <div className="mc-backdrop" onClick={() => setShowGarage(false)}>
          <div className="mc-garage-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="mc-garage-header">
              <h2 className="mc-garage-title">Add from my garage</h2>
              <button type="button" className="mc-iconbtn" onClick={() => setShowGarage(false)} aria-label="Close"><CloseIcon /></button>
            </div>
            {garage === null ? (
              <div className="mc-loading"><div className="loading-spinner" /></div>
            ) : garage.length === 0 ? (
              <p className="mc-muted">No vehicles in your garage yet.</p>
            ) : (
              <div className="mc-garage-list">
                {garage.map((uv) => {
                  const v = uv.vehicle || uv;
                  const label = [v.year, v.make, v.model].filter(Boolean).join(' ') || uv.nickname || 'Vehicle';
                  return (
                    <button type="button" key={uv.id} className="mc-garage-row" onClick={() => addFromGarage(uv)}>
                      <CarIcon size={18} />
                      <span className="mc-garage-row-text">{label}</span>
                      <PlusIcon />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
